import { observable } from '@trpc/server/observable';
import { type TRPCLink } from '@trpc/client';
import { TRPCClientError } from '@trpc/client';
import { type AppRouter } from "@/server/api/root";
import { signOut } from "@/lib/auth-client";
import { toast } from "react-toastify";

// Track the last time we triggered the unauthorized callback
let lastUnauthorizedCallback = 0;
const DEBOUNCE_INTERVAL = 10000; // 10 seconds debounce

/**
 * tRPC link that globally handles 401 errors and triggers a callback (e.g., redirect)
 * Includes debouncing to prevent multiple callbacks within a short time interval
 */
export function createAuthRedirectLink(): TRPCLink<AppRouter> {

    const debouncedOnUnauthorized = () => {
        const now = Date.now();
        if (now - lastUnauthorizedCallback > DEBOUNCE_INTERVAL) {
            lastUnauthorizedCallback = now;
            toast.error("Your session has expired, please sign in again to continue.");
            void signOut();
        }
    };

    return () => ({
        next,
        op
    }) => {
        return observable(observer => {
            const subscription = next(op).subscribe({
                next(result) {
                    observer.next(result);
                },
                error(err) {
                    if (
                        err instanceof TRPCClientError &&
                        typeof err.shape === 'object' &&
                        err.shape !== null &&
                        'data' in err.shape &&
                        (err.shape as { data?: { httpStatus?: number } }).data?.httpStatus === 401
                    ) {
                        debouncedOnUnauthorized();
                    }
                    observer.error(err);
                },
                complete() {
                    observer.complete();
                },
            });
            return () => subscription.unsubscribe();
        });
    };
}
